import styled from "@emotion/styled";
import React from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import AnimatedModal from "./AnimatedModal";
import { AppButton } from "./Generic/AppButton";

interface Props {
  title: React.ReactNode;
  message: React.ReactNode;
  initialCountdown: number;
  nextButtonText: string;
  closeButtonText: string;
  isOpen: boolean;
  onCountdownEnd: () => void;
  onClickNext: () => void;
  onRequestClose: () => void;
}

const CountdownModal = (props: Props) => {
  const [currentCountdown, setCurrentCountdown] = React.useState(() => props.initialCountdown);

  React.useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentCountdown((prevCountdown) => {
        if (prevCountdown === 0) {
          setTimeout(() => {
            props.onCountdownEnd.call(null);
            clearInterval(interval);
          });
          return prevCountdown;
        }

        return prevCountdown - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [props.initialCountdown, props.isOpen, props.onCountdownEnd]);

  const animation = (
    <CountdownModal.ProgressWrapper>
      <CircularProgressbar
        value={currentCountdown}
        maxValue={props.initialCountdown}
        strokeWidth={3}
        text={currentCountdown.toString()}
        background={true}
        counterClockwise={true}
        styles={circularProgressbarStyles}
      />
    </CountdownModal.ProgressWrapper>
  );

  return (
    <CountdownModal.Styled
      headline={props.title}
      message={props.message}
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
      animation={animation}
      animationOffset={85}
      actions={
        <>
          <AppButton onClick={props.onClickNext}>{props.nextButtonText}</AppButton>
          <AppButton variant="link" color="black" onClick={props.onRequestClose}>
            {props.closeButtonText}
          </AppButton>
        </>
      }
    />
  );
};

const circularProgressbarStyles = {
  root: {
    width: 100,
    height: 100,
  },
  background: {
    fill: "#FFF",
  },
  trail: {
    stroke: "var(--gray-100)",
  },
  path: {
    stroke: "var(--primary-color)",
    transitionDuration: "1s",
    transitionTimingFunction: "linear",
  },
  text: {
    fill: "var(--primary-color)",
    fontWeight: 700,
    fontSize: 32,
    transform: "translateY(4px)",
  },
};

CountdownModal.Styled = styled(AnimatedModal)``;

CountdownModal.ProgressWrapper = styled.div`
  padding: 8px;
  background-color: #fff;
  border-radius: 100px;
  display: inline-block;
  margin: auto;
`;

export default CountdownModal;
